import { defineStore } from 'pinia'
import { useRuntimeConfig } from '#app'
import Cookies from 'js-cookie'
import type { FetchError } from 'ofetch'
import { getBrandMinHotelStars, getBrandRequireHotelImages } from '~/server/config/siteConfig'

const STREAM_POLL_INTERVAL_MS = 1000  // Base polling interval when more data is available
const STREAM_POLL_WAITING_MS = 2000   // Longer interval when search in progress but no new data
const MAX_STREAM_ITERATIONS = 600
const MAX_FETCH_RETRIES = 2
const RETRYABLE_STATUS_CODES = new Set([408, 425, 429, 500, 502, 503, 504, 522, 524])
const RETRY_BACKOFF_BASE_MS = 500

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

const isAbortError = (error: unknown) =>
    Boolean(error) &&
    typeof error === 'object' &&
    'name' in (error as Record<string, unknown>) &&
    (error as Record<string, unknown>).name === 'AbortError'

const isTimeoutError = (error: unknown): boolean => {
    if (!error || typeof error !== 'object') {
        return false
    }
    const err = error as Record<string, unknown>
    // Check for timeout in error message (ofetch timeout errors)
    if (typeof err.message === 'string' && err.message.toLowerCase().includes('timeout')) {
        return true
    }
    // Check for timeout error name
    if (err.name === 'TimeoutError') {
        return true
    }
    // Check for cause containing timeout
    if (err.cause && typeof err.cause === 'object') {
        const cause = err.cause as Record<string, unknown>
        if (typeof cause.message === 'string' && cause.message.toLowerCase().includes('timeout')) {
            return true
        }
    }
    return false
}

const formatFetchError = (error: unknown): string => {
    // Always return a user-friendly message - don't expose technical details
    const defaultMessage = 'Unable to load hotels at the moment. Please try again.'

    if (!error || typeof error !== 'object') {
        return defaultMessage
    }

    const fetchError = error as FetchError
    const status = fetchError.status || fetchError.statusCode

    // Log the actual error for debugging but show friendly message to user
    console.error('[Hotel Search] Error details:', {
        status,
        message: fetchError.message,
        data: fetchError.data
    })

    // Return user-friendly messages based on status code
    if (status === 408 || status === 504 || status === 522 || status === 524) {
        return 'The search is taking longer than expected. Please try again.'
    }

    if (status === 422) {
        const detail = (fetchError as any).data?.detail || (fetchError as any).data?.message || ''
        if (typeof detail === 'string' && detail.toLowerCase().includes('past')) {
            return 'Your selected dates are in the past. Please choose future dates to search for availability.'
        }
        return 'Invalid search parameters. Please check your dates and try again.'
    }

    if (status === 429) {
        return 'Too many requests. Please wait a moment and try again.'
    }

    if (status === 503 || status === 502) {
        return 'Our hotel search service is temporarily unavailable. Please try again in a few moments.'
    }

    return defaultMessage
}

async function fetchWithRetry<T>(request: () => Promise<T>, { retries = MAX_FETCH_RETRIES } = {}) {
    let attempt = 0
    let lastError: unknown = null

    while (attempt <= retries) {
        try {
            return await request()
        } catch (error) {
            if (isAbortError(error)) {
                throw error
            }

            const fetchErr = error as FetchError
            const statusCode = fetchErr?.status ?? fetchErr?.statusCode ?? fetchErr?.response?.status
            const isRetryableStatus = statusCode && RETRYABLE_STATUS_CODES.has(statusCode)
            const shouldRetry = isRetryableStatus || isTimeoutError(error)

            if (!shouldRetry || attempt === retries) {
                throw error
            }

            lastError = error
            await sleep(RETRY_BACKOFF_BASE_MS * (attempt + 1))
            attempt += 1
        }
    }

    throw lastError
}

const getSuggestionKey = (suggestion: any): string | null => {
    if (!suggestion || typeof suggestion !== 'object') {
        return null
    }

    const keyCandidate =
        suggestion.product_id ||
        suggestion.hotel_id ||
        suggestion.id ||
        suggestion.uuid ||
        suggestion.slug ||
        suggestion.code

    if (keyCandidate) {
        return String(keyCandidate)
    }

    if (suggestion.hotel?.id) {
        return `hotel:${suggestion.hotel.id}`
    }

    if (suggestion.name) {
        return `${suggestion.name}-${suggestion.geo_place_id ?? suggestion.location_id ?? ''}`
    }

    return null
}

const DEFAULT_HOTEL_SORT = 'rating_desc'
const VALID_HOTEL_SORTS = new Set(['rating_desc', 'price_asc', 'price_desc', 'name_asc'])

const normalizeHotelSort = (sort: unknown): string | undefined => {
    if (typeof sort !== 'string' || !sort.length) {
        return undefined
    }

    return VALID_HOTEL_SORTS.has(sort) ? sort : undefined
}

//runtimeConfig

export const useSearchStore = defineStore('search', {
    state: () => {
        const config = useRuntimeConfig();
        return {
            searchResults: [] as any[],
            total: 0,
            selectedLocation: null as any,
            selectedCity: null as any,
            dates: [] as any[],
            formattedDates: [] as any[],
            quantity: 1,
            criteria: {
                adults: 2,
                children: 0,
                ages: [],
                date_start: null,
                date_end: null,
            } as any,
            hotelMoreIndicator: null,
            hotelProductsFirstPage: [] as any[],
            hotelsProducts: [],
            isFetchingHotels: false,
            hotelProductsLoadMore: false,
            loadingMoreHotelsProducts: false,
            searchPending: false,
            soldOutRooms: false,
            activeHotelSearchKey: '',
            hotelSearchError: null as string | null,
            isDynamicHotels: false, // Track if we're showing dynamic (with dates) or static hotels
            activitiesProducts: [] as any[],
            activitySearchDate: <string | null>null,
            loadingMoreActivities: false,
            host: config.public.baseURL,
            activityCategories: [],
            totalByCategory: [],
            searchInProgress: false,
            activityPage: 1,
            totalActivities: 0,
            activityAvailableDates: [],
            // Set when the supplier refuses to return dates until a pickup point is chosen
            // (ETL error 3103). The calendar must collect a pickup BEFORE it can show anything.
            activityPickupRequired: false,
            // Some suppliers enforce the pickup requirement at the PACKAGES layer instead, so the
            // dates call succeeds, 3103 never arrives, and the selector never renders — the
            // calendar then fills with bookable-looking dates that all answer 422. Activity 779888
            // ("...with Transfers") reproduces it: no pickup_id -> "No available packages", with
            // one -> full inventory. The product itself already knows, via is_pickup_required.
            activityPickupRequiredByProduct: false,
            activityPickups: [] as any[],
            activitySelectedPickupId: null as string | null,
            loadingRooms: false,
            bookingPeriod: 0,
            contextId: config.public.brand || 'tripening', // Add missing contextId
            filters: null as any, // Add missing filters property
            topActivities: [] as any[] // Add missing topActivities property
        }
    },
    getters: {
        getTotalVisitors(state) {
            return state.criteria.adults + state.criteria.children
        },
    },
    actions: {
        setQuantity(value: number) {
            this.quantity = value
        },
        setAdults(value: number) {
          this.criteria.adults = value
        },
        setChildren(value: number, ages: string) {
            this.criteria.children = value
            const agesStringArray = ages.split(',')
            const agesNumberArray = agesStringArray.map((age: string) => parseInt(age) || 1)
            this.criteria.ages = agesNumberArray

        },
        async getLocations(payload:{fts: string, domain: string, includePois?: boolean, includeAddresses?: boolean}) {

            try {
              const params = new URLSearchParams({
                fts: payload.fts,
                domain: payload.domain || 'hotel',
              })
              if (payload.includePois) {
                params.append('include_pois', 'true')
              }
              if (payload.includeAddresses) {
                params.append('include_addresses', 'true')
              }
              return await $fetch(`${this.host}/api/product/v1/search-suggestions?${params.toString()}`, {
                  method: 'GET',
                  headers: {
                    'X-TRIPENING-CONTEXT-ID': this.contextId,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                  },
                })
            } catch (error) {
                console.error('Fetch error:', error);
            }
        },
        setDates(value: any) {
            this.dates = value;
            this.criteria.date_start = value[0] || null
            this.criteria.date_end = value[1] || null
        },
        setFormattedDates(value: any) {
            this.formattedDates = value
        },
        setActivitySearchDate(value: string) {
          this.activitySearchDate = value
        },
        setCriteria(value: any) {
            this.criteria = value
        },
        async getCity(slug: string) {
            try {
                const response = await $fetch(`${this.host}/api/product/v1/geo-places/${slug}`, {
                    method: 'GET',
                    headers: {
                        'X-TRIPENING-CONTEXT-ID': this.contextId,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    },
                });
                this.selectedCity = response
                this.selectedLocation = response
                return response
            } catch (error) {
                this.selectedCity = null
                throw error
            }
        },
        /**
         * Apply the active brand's minimum-star floor to a user-supplied
         * rating_gte: returns max(user value, brand floor), or 0 when neither
         * applies. Brand floor is resolved from the deployment's siteName, the
         * same basis useBrand() uses, so other brands are unaffected.
         */
        _effectiveRatingGte(rating_gte: any): number {
            const rc = useRuntimeConfig()
            const brandMin = getBrandMinHotelStars(rc.public?.siteName as string | undefined)
            return Math.max(Number(rating_gte) || 0, brandMin)
        },
        /**
         * `has_images` for the active brand, or undefined when the brand has no
         * such requirement — spread into request bodies so non-curated brands
         * send no extra param at all.
         */
        _brandHasImages(): true | undefined {
            const rc = useRuntimeConfig()
            return getBrandRequireHotelImages(rc.public?.siteName as string | undefined)
        },

        async searchHotels(params: any) {
            const baseParams = { ...params }
            const normalizedSort = normalizeHotelSort(baseParams.sort) || DEFAULT_HOTEL_SORT
            const { is_mobile, token } = baseParams
            const { date_start, date_end, adults, children, ages } = baseParams
            const { rating_gte, price_lte, price_gte, amenities, hotel_name, provider } = baseParams || {}
            const effectiveRatingGte = this._effectiveRatingGte(rating_gte)
            const brandHasImages = this._brandHasImages()

            if (!date_start || !date_end) {
                return null
            }

            const isContinuation = Boolean(token)
            const searchKey = isContinuation && this.activeHotelSearchKey
                ? this.activeHotelSearchKey
                : `${Date.now()}-${Math.random().toString(36).slice(2)}`

            if (!isContinuation) {
                this.activeHotelSearchKey = searchKey
                this.searchPending = true
                this.loadingMoreHotelsProducts = false
                this.hotelProductsLoadMore = false
                ;(this as any)._hotelPlainIds = new Set<string>()
                ;(this as any)._hotelScoredIds = new Set<string>()
                ;(this as any)._scoredHotelList = []
                this.hotelSearchError = null
            } else {
                if (!this.activeHotelSearchKey) {
                    this.activeHotelSearchKey = searchKey
                }
                this.loadingMoreHotelsProducts = true
            }

            const ensureLatestSearch = () => this.activeHotelSearchKey === searchKey

            const createAbortController = () => {
                if (!isContinuation) {
                    const previous: AbortController | undefined = (this as any)._activeHotelsAbortController
                    if (previous) {
                        previous.abort('replaced')
                    }
                }

                const controller = new AbortController()
                ;(this as any)._activeHotelsAbortController = controller
                return controller
            }

            const abortController = createAbortController()

            const buildRequestPayload = (streamToken?: string) => {
                // For POI searches without a city, use coordinates
                const { poi_lat, poi_lon } = baseParams
                const hasGeoPlace = Boolean(this.selectedCity?.id)
                const hasPOICoords = poi_lat && poi_lon

                const payload: any = {
                    criteria: {
                        date_start,
                        date_end,
                        adults: adults ? parseFloat(adults) : 1,
                        ...(children && {
                            children: +children,
                            ages: +children > 0 ? ages : undefined
                        })
                    },
                    filters: {
                        // Use geo_place_id if available, otherwise use POI coordinates
                        ...(hasGeoPlace && { geo_place_id: this.selectedCity.id }),
                        ...(!hasGeoPlace && hasPOICoords && {
                            latitude: parseFloat(poi_lat),
                            longitude: parseFloat(poi_lon),
                            distance: 25000, // 25km radius for POI searches (in meters)
                        }),
                        ...(effectiveRatingGte && {
                            rating_gte: effectiveRatingGte,
                        }),
                        ...(price_lte && {
                            price_lte: +price_lte || null,
                        }),
                        ...(price_gte && {
                            price_gte: +price_gte || null,
                        }),
                        ...(amenities && {
                            amenities: amenities?.split(',') || null,
                        }),

                        ...(hotel_name && {
                            hotel_name: hotel_name || null,
                        }),
                        ...(provider && {
                            providers: [provider],
                        }),
                        ...(brandHasImages && {
                            has_images: true,
                        }),
                    },
                    ...(normalizedSort && { sort: normalizedSort }),
                    ...(is_mobile && { is_mobile }),
                }

                const effectiveToken = streamToken ?? token

                if (effectiveToken) {
                    payload.token = effectiveToken
                }

                if (baseParams.next) {
                    payload.next = baseParams.next
                }

                return payload
            }

            const mergePlainSuggestions = (suggestions: any[]) => {
                if (!Array.isArray(suggestions) || !suggestions.length) {
                    return
                }

                const plainSet: Set<string> = (this as any)._hotelPlainIds ?? new Set<string>()
                const scoredSet: Set<string> = (this as any)._hotelScoredIds ?? new Set<string>()

                ;(this as any)._hotelPlainIds = plainSet
                ;(this as any)._hotelScoredIds = scoredSet

                const deduped: any[] = []
                for (const suggestion of suggestions) {
                    const key = getSuggestionKey(suggestion)
                    if (!key) {
                        continue
                    }
                    if (plainSet.has(key) || scoredSet.has(key)) {
                        continue
                    }
                    plainSet.add(key)
                    deduped.push(suggestion)
                }

                if (!deduped.length) {
                    return
                }

                this.hotelsProducts = [...this.hotelsProducts, ...deduped] as any
            }

            const mergeScoredSuggestions = (suggestions: any[]) => {
                if (!Array.isArray(suggestions) || !suggestions.length) {
                    return
                }

                const scoredSet: Set<string> = (this as any)._hotelScoredIds ?? new Set<string>()
                let scoredList: any[] = (this as any)._scoredHotelList ?? []

                ;(this as any)._hotelScoredIds = scoredSet
                ;(this as any)._scoredHotelList = scoredList

                const newItems: any[] = []
                for (const suggestion of suggestions) {
                    const key = getSuggestionKey(suggestion)
                    if (!key || scoredSet.has(key)) {
                        continue
                    }
                    scoredSet.add(key)
                    newItems.push(suggestion)
                }

                if (!newItems.length) {
                    return
                }

                scoredList = [...scoredList, ...newItems]
                scoredList.sort((a, b) => (a.sorting_score ?? 0) - (b.sorting_score ?? 0))

                const perChunk = 40
                const chunked: any[][] = []
                for (let i = 0; i < scoredList.length; i += perChunk) {
                    chunked.push(scoredList.slice(i, i + perChunk))
                }

                this.hotelProductsFirstPage = chunked
                ;(this as any)._scoredHotelList = scoredList
            }

            let streamToken = token ?? null
            let iterations = 0
            let lastResponse: any = null
            let shouldContinue = true

            try {
                while (shouldContinue && iterations < MAX_STREAM_ITERATIONS) {
                    iterations += 1

                    if (!ensureLatestSearch()) {
                        break
                    }

                    const payload = buildRequestPayload(streamToken)

                    const response = await fetchWithRetry(() => $fetch(this.host + '/api/product/v3/hotels/search-availability', {
                        method: 'POST',
                        body: JSON.stringify(payload),
                        signal: abortController.signal,
                        headers: {
                            'X-TRIPENING-CONTEXT-ID': this.contextId,
                            'Content-Type': 'application/json',
                            'Accept': 'application/json',
                            'X-TRIPENING-CURRENCY': Cookies.get('currency') || 'USD',
                            // PPC margin token — ETL channel rules (rates must match the landing page)
                            ...(Cookies.get('_tripening_ppc') ? { 'X-PPC-Campaign': Cookies.get('_tripening_ppc') as string } : {})
                        },
                        timeout: 60000,
                    }) as any, {
                        retries: isContinuation ? 1 : MAX_FETCH_RETRIES
                    })

                    lastResponse = response

                    if (!ensureLatestSearch()) {
                        break
                    }

                    const suggestions = Array.isArray((response as any)?.suggestions) ? (response as any).suggestions : []
                    this.total = (response as any)?.total_suggestions ?? this.total
                    this.searchInProgress = Boolean((response as any)?.searching_in_progress)

                    if (suggestions.length) {
                        if (this.hotelSearchError) {
                            this.hotelSearchError = null
                        }
                        
                        const scored: any[] = []
                        const plain: any[] = []

                        for (const suggestion of suggestions) {
                            if (suggestion?.sorting_score !== null && suggestion?.sorting_score !== undefined) {
                                scored.push(suggestion)
                            } else {
                                plain.push(suggestion)
                            }
                        }

                        mergeScoredSuggestions(scored)
                        mergePlainSuggestions(plain)
                    }

                    if ((response as any)?.filters && (!this.filters || !isContinuation)) {
                        await this.getHotelFilters((response as any).filters)
                    }

                    if ((response as any)?.next) {
                        this.hotelMoreIndicator = (response as any).next
                    } else if (!(response as any)?.searching_in_progress && !(response as any)?.more) {
                        this.hotelMoreIndicator = null
                    }

                    // Determine if we should continue polling and what delay to use
                    let pollDelay = STREAM_POLL_INTERVAL_MS

                    if ((response as any)?.more) {
                        // More data immediately available - use shorter delay
                        streamToken = (response as any).more
                        shouldContinue = true
                        pollDelay = STREAM_POLL_INTERVAL_MS
                    } else if ((response as any)?.next) {
                        // Next page of results available - fetch it
                        streamToken = (response as any).next
                        shouldContinue = true
                        pollDelay = (response as any)?.searching_in_progress ? STREAM_POLL_INTERVAL_MS : STREAM_POLL_INTERVAL_MS
                    } else if ((response as any)?.searching_in_progress) {
                        // Search in progress but no new data - use longer delay to reduce server load
                        shouldContinue = true
                        pollDelay = STREAM_POLL_WAITING_MS
                    } else {
                        shouldContinue = false
                    }

                    if (shouldContinue) {
                        await sleep(pollDelay)
                    }
                }
            } catch (error) {
                if (isAbortError(error)) {
                    return null
                }

                if (!ensureLatestSearch()) {
                    return null
                }

                this.hotelSearchError = formatFetchError(error)
                this.searchPending = false
                this.loadingMoreHotelsProducts = false
                this.searchInProgress = false

                console.error('[search] Failed to load hotels:', error)
                return null
            } finally {
                if ((this as any)._activeHotelsAbortController === abortController) {
                    (this as any)._activeHotelsAbortController = null
                }

                // Always reset loading flags — even for superseded searches
                this.loadingMoreHotelsProducts = false

                if (ensureLatestSearch()) {
                    this.searchPending = false
                    this.searchInProgress = false
                }
            }

            if (lastResponse) {
                return lastResponse
            }

            return {
                suggestions: [],
                more: null,
                next: this.hotelMoreIndicator,
                searching_in_progress: false
            }
        },
        setHotelsProducts(value: any) {
            this.hotelsProducts = value
        },
        setActivitiesProducts(value: any) {
            this.activitiesProducts = value
        },
        setFilteredHotelByScore(_products: any, limit: any) {
            if (!Array.isArray(_products) || !_products.length) {
                return
            }

            const perChunk = limit || 40
            const scoredSet: Set<string> = (this as any)._hotelScoredIds ?? new Set<string>()
            let scoredList: any[] = (this as any)._scoredHotelList ?? []

            ;(this as any)._hotelScoredIds = scoredSet
            ;(this as any)._scoredHotelList = scoredList

            const newItems: any[] = []
            for (const item of _products) {
                const key = getSuggestionKey(item)
                if (!key || scoredSet.has(key)) {
                    continue
                }
                scoredSet.add(key)
                newItems.push(item)
            }

            if (!newItems.length) {
                return
            }

            scoredList = [...scoredList, ...newItems]
            scoredList.sort((a, b) => (a.sorting_score ?? 0) - (b.sorting_score ?? 0))

            const chunked: any[][] = []
            for (let i = 0; i < scoredList.length; i += perChunk) {
                chunked.push(scoredList.slice(i, i + perChunk))
            }

            this.hotelProductsFirstPage = chunked
            ;(this as any)._scoredHotelList = scoredList
        },
        async getHotelFilters(token: string) {
            const _url = `${this.host}/api/product/v1/hotels/available-filters`
            try {
                const response = await $fetch(_url, {
                    method: 'GET',
                    params: { token: token },
                    headers: {
                        'X-TRIPENING-CONTEXT-ID': this.contextId,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'X-TRIPENING-CURRENCY': Cookies.get('currency') || 'USD'
                    },
                });

                this.filters = response
                return response;
            } catch (error) {
                console.error('Fetch error:', error);
            }
        },
        setFiltersFromQuery(value: any) {
            const { price_lte, price_gte, rating_gte, amenities } = value

            //get amenities from query
            this.filters = {
                price_max: {
                    amount: price_gte ? +price_gte : null,
                    currency: "USD" //temporary hardcoded
                },
                price_min: {
                    amount: price_lte ? +price_lte : null,
                    currency: "USD" //temporary hardcoded
                },
                rating_max: 5,
                rating_min: rating_gte || null
            }
        },
        setSelectedLocation(data: any) {
            // console.log('setSelectedLocation', data)
        },
        setSearchResults(value: any) {
            this.searchResults = value
        },
        setSoldOutRooms(value:boolean) {
          this.soldOutRooms = value
        },
        setLoadingRooms(value: boolean) {
          this.loadingRooms = value
        },
        clearHotelsResults() {
            this.hotelProductsFirstPage = []
            this.hotelsProducts = []
            this.hotelMoreIndicator = null
            this.total = 0
            this.searchInProgress = false
            this.searchPending = false
            this.loadingMoreHotelsProducts = false
            this.activeHotelSearchKey = ''

            ;(this as any)._hotelPlainIds = new Set<string>()
            ;(this as any)._hotelScoredIds = new Set<string>()
            ;(this as any)._scoredHotelList = []
        },
        clearActivityResults() {
            this.activitiesProducts = []
            this.totalActivities = 0
        },
        setBookingPeriod(value: number) {
          const normalizedValue = Number.isFinite(value) ? Math.abs(value) : 0
          this.bookingPeriod = normalizedValue
        },
        async searchTopActivities() {
          try {
            const response = await $fetch(this.host + '/api/product/v3/top-rated-activities?size=3', {
              method: 'GET',
              headers: {
                'X-TRIPENING-CONTEXT-ID': this.contextId,
                'Content-Type': 'application/json',
                'Accept': 'application/json',
              },
            })
            this.topActivities = (response as any)?.activities || []
            return response;
          } catch (e) {
            console.log(e)
          }
        },
        async searchActivities(params: any) {
          const { rating_gte, price_lte, price_gte, categories, append } = params || {}

          if (!append) {
            this.searchPending = true
          }

          const queryParams: any = {
            place_id: this.selectedCity?.id,
            page: params.page || 1,
            sort_by: params.sort || 'rating_desc',
            size: params.size || 20,
          }

          this.activityPage = params.page || 1

          if (price_lte) {
            queryParams['price_lte'] = price_lte
          }
          if (price_gte) {
            queryParams['price_gte'] = price_gte
          }
          if (rating_gte) {
            queryParams['rating_gte'] = rating_gte
          }

          if (categories) {
            queryParams['categories'] = categories
          }

          try {
            const response = await $fetch(this.host + '/api/product/v3/activities', {
              method: 'GET',
              params: queryParams,
              headers: {
                'X-TRIPENING-CONTEXT-ID': this.contextId,
                'X-TRIPENING-CURRENCY': Cookies.get('currency') || 'USD'
              },
            })

            const { activities, total, total_by_category } = (response as any) || {}
            if (total !== undefined) {
              this.totalActivities = total
            }
            if (total_by_category) {
              this.totalByCategory = total_by_category
            }
            if (activities?.length) {
              if (append) {
                this.activitiesProducts = [...this.activitiesProducts, ...activities]
              } else {
                this.setActivitiesProducts(activities)
              }
            }
            this.loadingMoreActivities = false
            this.searchPending = false
            return response
          } catch (error) {
            this.searchPending = false
            this.loadingMoreActivities = false
            throw error
          }
        },
        async searchActivitiesDates({ params, id, _walk = 0 }: any): Promise<any> {
          this.activityAvailableDates = []
          try {
            const response = await $fetch(this.host + `/api/product/v1/activities/${id}/dates`, {
              method: 'GET',
              params: params,
              headers: {
                'X-TRIPENING-CONTEXT-ID': this.contextId,
                'X-TRIPENING-CURRENCY': Cookies.get('currency') || 'USD'
              },
            })
            if ((response as any)?.dates) {
              this.activityAvailableDates = (response as any)?.dates
            }
            // A successful dates call clears only the SUPPLIER-signalled requirement. Blindly
            // setting false here also wiped the product-level one seeded on mount, which is the
            // other half of the 779888 bug.
            this.activityPickupRequired = this.activityPickupRequiredByProduct
            return response
          } catch (error: any) {
            // 3103: the supplier will not return ANY dates until a pickup point is chosen.
            // This is NOT "sold out" — treat it as a prompt, not a failure, or the calendar
            // silently refuses to open (it did, on every pickup-required product).
            const errs = error?.data?.errors || error?.response?._data?.errors || []
            if (errs.some((e: any) => e?.code === 3103)) {
              this.activityPickupRequired = true
              await this.searchActivityPickups(id)
              return null
            }
            // 3100 "No available dates" is returned when the requested start_date itself has
            // no availability — NOT when the activity is unbookable. The calendar always asks
            // from today, so any activity without same-day availability returned 422 and the
            // calendar never opened, even with hundreds of bookable dates a few days out
            // (779915: today -> 422, tomorrow -> 613 dates through 2028).
            // Walk the start date forward before giving up.
            const WALK_DAYS: number[] = [1, 7, 30, 90]
            const step = WALK_DAYS[_walk] ?? 0
            if (step && errs.some((e: any) => e?.code === 3100)) {
              const from = new Date(params?.start_date || new Date())
              from.setDate(from.getDate() + step)
              const next = { ...params, start_date: from.toISOString().slice(0, 10) }
              return await this.searchActivitiesDates({ params: next, id, _walk: _walk + 1 })
            }
            throw error
          }
        },
        // Seed the pickup requirement from the product before any availability call, so
        // products whose supplier only enforces it at the packages layer still prompt for a
        // pickup instead of rendering a calendar nothing can be booked from.
        async setActivityPickupRequirement(required: boolean, id: any) {
          this.activityPickupRequiredByProduct = !!required
          if (!required) return
          this.activityPickupRequired = true
          await this.searchActivityPickups(id)
        },
        async searchActivityPickups(id: any) {
          this.activityPickups = []
          try {
            const response = await $fetch(this.host + `/api/product/v1/activities/${id}/pickups`, {
              method: 'GET',
              headers: {
                'X-TRIPENING-CONTEXT-ID': this.contextId,
                'X-TRIPENING-CURRENCY': Cookies.get('currency') || 'USD'
              },
            })
            this.activityPickups = (response as any)?.pickups || []
            return this.activityPickups
          } catch (error) {
            throw error
          }
        },
        async searchActivitiesPackages({ id, date, payload = {} }: any) {
          try {
            const response = await $fetch(this.host + `/api/product/v3/activities/${id}/dates/${date}`, {
              method: 'POST',
              body: payload,
              headers: {
                'X-TRIPENING-CONTEXT-ID': this.contextId,
                'X-TRIPENING-CURRENCY': Cookies.get('currency') || 'USD'
              },
            })
            return response
          } catch (error) {
            throw error
          }
        },
        async getActivityCategories() {
          this.activityCategories = []
          try {
            const res = await $fetch(this.host + '/api/product/v1/activities/categories', {
              headers: {
                'X-TRIPENING-CONTEXT-ID': this.contextId
              }
            })
            this.activityCategories = res as any
            return res
          } catch (e) {
            console.log(e)
          }
        },
        async searchStaticHotels(params: any) {
            // Static hotel search - fetches hotels without availability/pricing
            // Used when no dates are selected
            if (!this.selectedCity?.id && params.citySlug) {
                try {
                    await this.getCity(params.citySlug)
                } catch (e) {
                    console.warn('Failed to resolve city for static search:', params.citySlug)
                }
            }
            if (!this.selectedCity?.id) {
                console.error('No city selected for static hotel search')
                return { hotels: [], total: 0 }
            }

            const HOTEL_ITEMS_PER_PAGE_DEFAULT = 40
            const { rating_gte, hotel_name } = params
            const effectiveRatingGte = this._effectiveRatingGte(rating_gte)
            const sort = normalizeHotelSort(params?.sort)

            const data = {
                limit: HOTEL_ITEMS_PER_PAGE_DEFAULT,
                rating_gte: effectiveRatingGte,
                geo_place_id: this.selectedCity.id,
                page: 1,
                sort_by: sort,
                fts: hotel_name || undefined,
                has_images: this._brandHasImages(),
            }

            try {
                this.searchPending = true
                const response = await $fetch(`${this.host}/api/product/v3/hotels/search`, {
                    method: 'POST',
                    body: data,
                    headers: {
                        'X-TRIPENING-CONTEXT-ID': this.contextId,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    },
                })

                if ((response as any)?.hotels?.length) {
                    const formattedHotels = (response as any).hotels.map((hotel: any) => ({
                        hotel: hotel,
                        cheapest_rate: null,
                        price_from: null,
                        static: true
                    }))

                    this.hotelsProducts = formattedHotels
                    this.total = (response as any).total || formattedHotels.length
                    this.searchPending = false
                    return response
                } else {
                    this.hotelsProducts = []
                    this.total = 0
                    this.searchPending = false
                    return { hotels: [], total: 0 }
                }
            } catch (error) {
                console.error('Static hotel search error:', error)
                this.searchPending = false
                this.hotelSearchError = formatFetchError(error)
                throw error
            }
        },

        async loadMoreStaticHotels(params: any) {
            // Append next page of static hotels (used by "Load more" button)
            if (!this.selectedCity?.id) return

            const HOTEL_ITEMS_PER_PAGE_DEFAULT = 40
            const { page, rating_gte, hotel_name } = params
            const effectiveRatingGte = this._effectiveRatingGte(rating_gte)
            const sort = normalizeHotelSort(params?.sort)

            try {
                const response = await $fetch(`${this.host}/api/product/v3/hotels/search`, {
                    method: 'POST',
                    body: {
                        limit: HOTEL_ITEMS_PER_PAGE_DEFAULT,
                        rating_gte: effectiveRatingGte,
                        geo_place_id: this.selectedCity.id,
                        page: page || 1,
                        sort_by: sort,
                        fts: hotel_name || undefined,
                        has_images: this._brandHasImages(),
                    },
                    headers: {
                        'X-TRIPENING-CONTEXT-ID': this.contextId,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    },
                })

                const newHotels = ((response as any)?.hotels || []).map((hotel: any) => ({
                    hotel,
                    cheapest_rate: null,
                    price_from: null,
                    static: true
                }))

                if (newHotels.length) {
                    const existingIds = new Set(this.hotelsProducts.map((h: any) => h.hotel?.id))
                    const deduped = newHotels.filter((h: any) => !existingIds.has(h.hotel?.id))
                    this.hotelsProducts = [...this.hotelsProducts, ...deduped] as any
                }
            } catch (error) {
                console.error('Load more static hotels error:', error)
            }
        },

        async loadAllStaticHotelsForMap(params: any) {
            // Progressively load all static hotels for map view (100 per page)
            if (!this.selectedCity?.id) {
                return
            }
            if (this.loadingMoreHotelsProducts) {
                // Don't stack concurrent loads, but don't silently skip either
                return
            }

            const MAP_PAGE_SIZE = 100
            const { rating_gte, hotel_name } = params
            const effectiveRatingGte = this._effectiveRatingGte(rating_gte)
            const sort = normalizeHotelSort(params?.sort)

            this.loadingMoreHotelsProducts = true

            try {
                // First page to get total and initial results
                const firstResponse = await $fetch(`${this.host}/api/product/v3/hotels/search`, {
                    method: 'POST',
                    body: {
                        limit: MAP_PAGE_SIZE,
                        rating_gte: effectiveRatingGte,
                        geo_place_id: this.selectedCity.id,
                        page: 1,
                        sort_by: sort,
                        fts: hotel_name || undefined,
                        has_images: this._brandHasImages(),
                    },
                    headers: {
                        'X-TRIPENING-CONTEXT-ID': this.contextId,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    },
                })

                const total = (firstResponse as any).total || 0
                const firstHotels = (firstResponse as any).hotels || []
                this.total = total

                // Collect all hotels with deduplication by ID
                const seenIds = new Set<string>()
                const allHotels: any[] = []

                for (const hotel of firstHotels) {
                    const id = hotel.id?.toString()
                    if (id && !seenIds.has(id)) {
                        seenIds.add(id)
                        allHotels.push({ hotel, cheapest_rate: null, price_from: null, static: true })
                    }
                }

                // Update store progressively after first page
                this.hotelsProducts = [...allHotels] as any

                // Load remaining pages
                const totalPages = Math.ceil(total / MAP_PAGE_SIZE)
                for (let page = 2; page <= totalPages; page++) {
                    const response = await $fetch(`${this.host}/api/product/v3/hotels/search`, {
                        method: 'POST',
                        body: {
                            limit: MAP_PAGE_SIZE,
                            rating_gte: effectiveRatingGte,
                            geo_place_id: this.selectedCity.id,
                            page,
                            sort_by: sort,
                            fts: hotel_name || undefined,
                            has_images: this._brandHasImages(),
                        },
                        headers: {
                            'X-TRIPENING-CONTEXT-ID': this.contextId,
                            'Content-Type': 'application/json',
                            'Accept': 'application/json',
                        },
                    })

                    const hotels = (response as any).hotels || []
                    for (const hotel of hotels) {
                        const id = hotel.id?.toString()
                        if (id && !seenIds.has(id)) {
                            seenIds.add(id)
                            allHotels.push({ hotel, cheapest_rate: null, price_from: null, static: true })
                        }
                    }

                    // Update store after each page so map markers appear progressively
                    this.hotelsProducts = [...allHotels] as any
                }
            } catch (error) {
                console.error('Error loading static hotels for map:', error)
            } finally {
                this.loadingMoreHotelsProducts = false
            }
        }
    },
})
